/* Saduq Care — section-pages.js
   Collection facets (Section Rendering API), grid/list view, cart note,
   FAQ tabs + search, customer address toggles / provinces / delete confirm.
   Loaded (deferred) by several sections — guarded so it only runs once. */
(() => {
  'use strict';
  if (window.__pagesJs) return;
  window.__pagesJs = true;

  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const store = {
    get(k) { try { return localStorage.getItem(k); } catch (_) { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch (_) {} }
  };

  /* ================= Collection: facets ================= */
  const VIEW_KEY = 'sc-coll-view';

  function applyView(root, view) {
    view = view || store.get(VIEW_KEY) || 'grid';
    $$('[data-coll-grid]', root).forEach((g) => g.classList.toggle('is-list', view === 'list'));
    $$('[data-view]', root).forEach((b) => {
      const on = b.dataset.view === view;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function paramsFromForm(form) {
    const p = new URLSearchParams();
    new FormData(form).forEach((v, k) => {
      if (String(v).trim() !== '') p.append(k, v);
    });
    return p;
  }

  let ctrl;
  async function renderFacets(root, url) {
    const u = new URL(url, window.location.origin);
    u.searchParams.delete('section_id');
    u.searchParams.delete('page');
    const fetchUrl = new URL(u.toString());
    fetchUrl.searchParams.set('section_id', root.dataset.sectionId);

    // remember which facet groups are collapsed
    const closed = $$('details[data-facet-key]:not([open])', root).map((d) => d.dataset.facetKey);
    const expanded = $$('.facet__body.is-expanded', root).map((b) => b.closest('details').dataset.facetKey);

    root.classList.add('is-loading');
    ctrl && ctrl.abort();
    ctrl = new AbortController();
    try {
      const res = await fetch(fetchUrl.toString(), { signal: ctrl.signal });
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      $$('[data-facets-part]', root).forEach((el) => {
        const fresh = doc.querySelector(`[data-facets-part="${el.dataset.facetsPart}"]`);
        if (fresh) el.innerHTML = fresh.innerHTML;
      });
      $$('details[data-facet-key]', root).forEach((d) => {
        if (closed.includes(d.dataset.facetKey)) d.open = false;
        if (expanded.includes(d.dataset.facetKey)) {
          const b = d.querySelector('.facet__body');
          b && b.classList.add('is-expanded');
        }
      });
      window.history.replaceState({}, '', u.pathname + (u.search ? u.search : ''));
      applyView(root);
      if (window.themeBoot) window.themeBoot(root);
    } catch (err) {
      if (err.name !== 'AbortError') window.location.href = u.toString();
    } finally {
      root.classList.remove('is-loading');
    }
  }

  const collRoot = (el) => el.closest('[data-coll][data-section-id]');

  // checkbox filters apply immediately
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-facet-auto]');
    if (input) {
      const form = input.closest('[data-facets-form]');
      const root = collRoot(input);
      if (!form || !root) return;
      renderFacets(root, `${form.getAttribute('action')}?${paramsFromForm(form)}`);
      return;
    }
    const sort = e.target.closest('[data-sort-select]');
    if (sort) {
      const root = collRoot(sort);
      $$('[data-sort-hidden]', document).forEach((h) => (h.value = sort.value));
      const u = new URL(window.location.href);
      u.searchParams.set('sort_by', sort.value);
      if (root) renderFacets(root, u.toString());
      else window.location.href = u.toString();
    }
  });

  // price "apply" (form submit)
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-facets-form]');
    if (!form) return;
    const root = collRoot(form);
    if (!root) return;
    e.preventDefault();
    renderFacets(root, `${form.getAttribute('action')}?${paramsFromForm(form)}`);
  });

  document.addEventListener('click', (e) => {
    // remove-filter / clear-all links
    const link = e.target.closest('a[data-facet-link]');
    if (link) {
      const root = collRoot(link);
      if (root) {
        e.preventDefault();
        renderFacets(root, link.href);
      }
      return;
    }
    // show more values
    const more = e.target.closest('[data-facet-more]');
    if (more) {
      const body = more.closest('.facet__body');
      body && body.classList.toggle('is-expanded');
      return;
    }
    // grid / list
    const vbtn = e.target.closest('[data-view]');
    if (vbtn) {
      const root = collRoot(vbtn);
      if (!root) return;
      store.set(VIEW_KEY, vbtn.dataset.view);
      applyView(root, vbtn.dataset.view);
    }
  });

  /* ================= Cart: save note ================= */
  let noteTimer;
  document.addEventListener('input', (e) => {
    const note = e.target.closest('[data-cart-note]');
    if (!note) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      const routes = (window.theme && window.theme.routes) || {};
      fetch(`${routes.cartUpdate || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note.value })
      }).catch(() => {});
    }, 500);
  });

  /* ================= FAQ tabs + search ================= */
  function faqFilter(root) {
    const input = root.querySelector('[data-faqx-search]');
    const q = input ? input.value.trim().toLowerCase() : '';
    const active = root.querySelector('[data-faqx-tab].is-active');
    const tab = active ? active.dataset.faqxTab : '';
    let shown = 0;
    $$('[data-faqx-item]', root).forEach((item) => {
      let show;
      if (q) show = item.textContent.toLowerCase().includes(q);
      else show = !tab || !item.dataset.tab || item.dataset.tab === tab;
      item.hidden = !show;
      if (show) shown++;
    });
    const none = root.querySelector('[data-faqx-none]');
    if (none) none.hidden = shown > 0;
    $$('[data-faqx-tab]', root).forEach((b) => b.classList.toggle('is-dim', !!q));
  }
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-faqx-tab]');
    if (!b) return;
    const root = b.closest('[data-faqx]');
    $$('[data-faqx-tab]', root).forEach((x) => {
      x.classList.toggle('is-active', x === b);
      x.setAttribute('aria-selected', x === b ? 'true' : 'false');
    });
    const input = root.querySelector('[data-faqx-search]');
    if (input) input.value = '';
    faqFilter(root);
    const first = $$('[data-faqx-item]:not([hidden])', root)[0];
    if (first) first.open = true;
  });
  document.addEventListener('input', (e) => {
    const i = e.target.closest('[data-faqx-search]');
    if (i) faqFilter(i.closest('[data-faqx]'));
  });

  /* ================= Generic toggles (addresses) ================= */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-pg-toggle]');
    if (!t) return;
    const el = document.getElementById(t.dataset.pgToggle);
    if (!el) return;
    el.hidden = !el.hidden;
    $$(`[data-pg-toggle="${t.dataset.pgToggle}"][aria-expanded]`).forEach((x) => x.setAttribute('aria-expanded', el.hidden ? 'false' : 'true'));
    if (!el.hidden) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      const f = el.querySelector('input, select');
      f && f.focus({ preventScroll: true });
    }
  });

  document.addEventListener('submit', (e) => {
    const f = e.target.closest('form[data-confirm]');
    if (f && !window.confirm(f.dataset.confirm)) e.preventDefault();
  });

  /* country -> province selects (all_country_option_tags carry data-provinces) */
  function initCountry(sel) {
    if (sel.dataset.pgReady) return;
    sel.dataset.pgReady = '1';
    const prov = document.getElementById(sel.dataset.pgCountry);
    const wrap = prov && prov.closest('[data-pg-province-wrap]');
    if (sel.dataset.default) {
      const opt = Array.from(sel.options).find((o) => o.value === sel.dataset.default || o.text === sel.dataset.default);
      if (opt) sel.value = opt.value;
    }
    const fill = (keep) => {
      if (!prov) return;
      const opt = sel.options[sel.selectedIndex];
      let list = [];
      try { list = JSON.parse((opt && opt.dataset.provinces) || '[]'); } catch (_) {}
      prov.innerHTML = list.map((p) => `<option value="${p[0]}">${p[1]}</option>`).join('');
      if (wrap) wrap.hidden = list.length === 0;
      if (keep && prov.dataset.default) {
        const o = Array.from(prov.options).find((x) => x.value === prov.dataset.default || x.text === prov.dataset.default);
        if (o) prov.value = o.value;
      }
    };
    fill(true);
    sel.addEventListener('change', () => fill(false));
  }

  /* ================= Boot ================= */
  function boot(scope = document) {
    $$('[data-coll][data-section-id]', scope).forEach((r) => applyView(r));
    if (scope.matches && scope.matches('[data-coll]')) applyView(scope);
    $$('select[data-pg-country]', scope).forEach(initCountry);
    // re-open an address form that came back with errors
    $$('[data-open-on-load]', scope).forEach((m) => {
      const box = m.closest('[hidden]');
      if (box) box.hidden = false;
    });
    $$('[data-faqx]', scope).forEach(faqFilter);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
